import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setBusinessUnit, setSelectedCycleId } from '../../redux/HomePage/cyclesByBuSlice';
import { setComprehensiveDataRx, setStepperTaskRx } from '../../redux/Calendar/calendarSlice';
import { useOkta } from '../../hooks/oktaHook';
import { GENERAL_USER } from '../../utils/Constants';
import { fetchListOfActiveBUs, fetchUserDetails } from './service';
import CustomSkeleton from '../../components/Skeleton/CustomSkeleton';
import './HomePage.css';
import { getUrlWithParams } from '../../utils/Utils';

const HomeTextComponent = () => {
  const { userDetails } = useOkta();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);
  const [buList, setBuList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const params = new URLSearchParams(search);

  // useEffect to update bu and buList once userDetails is available
  useEffect(() => {
    if (userDetails?.BUs?.length) {
      setIsLoading(true);
      fetchListOfActiveBUs().then(async (response) => {
        const activeBus = response?.filter((item) => item.isActive).map((item) => item.bus);
        const filteredList = userDetails.BUs.filter((item) => activeBus.includes(item));

        filteredList.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        setBuList(filteredList || []);

        const previouslySelectedBu = selectedBu || '';
        let defaultSelectedBu = '';

        if (filteredList && filteredList.length > 0) {
          if (filteredList.includes(previouslySelectedBu)) {
            defaultSelectedBu = previouslySelectedBu;
          } else {
            defaultSelectedBu = filteredList[0];
          }
        }

        // check if the user exists in the db
        fetchUserDetails(userDetails?.email).then((res) => {
          if(res !== null) {
            navigate(getUrlWithParams('/home', defaultSelectedBu));
          }
        })
        dispatch(setBusinessUnit(defaultSelectedBu));
        setIsLoading(false);
      });
    }
  }, [userDetails?.BUs]);

  const handleChange = (event) => {
    dispatch(setBusinessUnit(event.target.value));
    dispatch(setComprehensiveDataRx([]));
    dispatch(setStepperTaskRx([]));
    dispatch(setSelectedCycleId(null));
    localStorage.setItem('cycleId', null);
    localStorage.setItem('cycleName', '');
    navigate(`/home?bu=${encodeURIComponent(event.target.value)}`);
  };

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Stack gap="24px" sx={{ width: '100%' }}>
          <Typography variant="h4">Portfolio Optimization</Typography>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: '16px', height: 'fit-content' }}>
          <FormControl variant="outlined" fullWidth>
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '2px 5px',
                  borderRadius: '8px',
                  border: '1px solid #DBEAFE',
                  backgroundColor: '#F8FAFC',
                  justifyContent: 'center', // Center horizontally
                  alignItems: 'center', // Center vertically
                  width: '15rem',
                  height: '2rem'
                }}>
                <CustomSkeleton numberOfSkeleton={1} />
              </Box>
            ) : (
              <Select
                data-testid="bu-selector"
                fontWeight={400}
                value={selectedBu}
                onChange={handleChange}
                disabled={userDetails?.roles?.includes(GENERAL_USER)}
                sx={{
                  minWidth: '107px',
                  height: '32px',
                  backgroundColor: 'rgba(251, 252, 254, 1)',
                  '.MuiSelect-icon': {
                    color: 'rgba(58, 58, 58, 1)'
                  }
                }}>
                {buList?.map((buItem, index) => (
                  <MenuItem value={buItem} key={`${index +1}`}>
                    {buItem}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
        </Stack>
      </Stack>
      <Typography variant="body1" className="homepage-description-text">
        Welcome to your Portfolio Optimization workspace. Access the PO reports and analytics
        from the cards below. Start by uploading your local portfolio data file to initiate PO
        cycle or select cycle data file from the drop down on the right.
      </Typography>
    </Box>
  );
};

export default HomeTextComponent;
